import { supabase } from './supabase';

export interface VehicleMake {
  id: string;
  name: string;
  popular: boolean;
  created_at: string;
}

export interface VehicleModel {
  id: string;
  make_id: string;
  name: string;
  year_from?: number;
  year_to?: number;
  default_tyre_size?: string;
  created_at: string;
  make_name?: string;
}

export interface VehicleSearchParams {
  make?: string;
  model?: string;
  year?: number;
  size?: string;
}

/**
 * Get vehicle make suggestions based on search query
 */
export const getVehicleMakeSuggestions = async (query: string = ''): Promise<VehicleMake[]> => {
  try {
    let queryBuilder = supabase
      .from('vehicle_makes')
      .select('*')
      .order('popular', { ascending: false })
      .order('name', { ascending: true });

    if (query.trim()) {
      queryBuilder = queryBuilder.ilike('name', `%${query.trim()}%`);
    }

    const { data, error } = await queryBuilder.limit(10);

    if (error) {
      console.error('Error fetching vehicle makes:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Failed to fetch vehicle make suggestions:', err);
    return [];
  }
};

/**
 * Get vehicle model suggestions based on make and search query
 */
export const getVehicleModelSuggestions = async (
  makeId?: string, 
  makeName?: string, 
  query: string = ''
): Promise<VehicleModel[]> => {
  try {
    let queryBuilder = supabase
      .from('vehicle_models')
      .select(`
        *,
        vehicle_makes!inner(name)
      `)
      .order('name', { ascending: true });

    // Filter by make if provided
    if (makeId) {
      queryBuilder = queryBuilder.eq('make_id', makeId);
    } else if (makeName) {
      queryBuilder = queryBuilder.eq('vehicle_makes.name', makeName);
    }

    // Filter by model name if query provided
    if (query.trim()) {
      queryBuilder = queryBuilder.ilike('name', `%${query.trim()}%`);
    }

    const { data, error } = await queryBuilder.limit(15);

    if (error) {
      console.error('Error fetching vehicle models:', error);
      return [];
    }

    // Transform the data to include make_name
    return (data || []).map((model: any) => ({
      ...model,
      make_name: model.vehicle_makes?.name || 'Unknown'
    }));
  } catch (err) {
    console.error('Failed to fetch vehicle model suggestions:', err);
    return [];
  }
};

/**
 * Search for vehicles based on criteria
 */
export const searchVehicles = async (params: VehicleSearchParams): Promise<VehicleModel[]> => {
  try {
    let queryBuilder = supabase
      .from('vehicle_models')
      .select(`
        *,
        vehicle_makes!inner(name)
      `);

    // Filter by make
    if (params.make) {
      queryBuilder = queryBuilder.eq('vehicle_makes.name', params.make);
    }

    // Filter by model
    if (params.model) {
      queryBuilder = queryBuilder.ilike('name', `%${params.model}%`);
    }

    // Filter by year range
    if (params.year) {
      queryBuilder = queryBuilder
        .or(`year_from.is.null,year_from.lte.${params.year}`)
        .or(`year_to.is.null,year_to.gte.${params.year}`);
    }

    // Filter by tyre size
    if (params.size) {
      queryBuilder = queryBuilder.eq('default_tyre_size', params.size);
    }

    const { data, error } = await queryBuilder
      .order('vehicle_makes.name', { ascending: true })
      .order('name', { ascending: true })
      .limit(50);

    if (error) {
      console.error('Error searching vehicles:', error);
      return [];
    }

    // Transform the data to include make_name
    return (data || []).map((model: any) => ({
      ...model,
      make_name: model.vehicle_makes?.name || 'Unknown'
    }));
  } catch (err) {
    console.error('Failed to search vehicles:', err);
    return [];
  }
};

/**
 * Get compatible tyres for a specific vehicle
 */
export const getCompatibleTyres = async (
  defaultTyreSize?: string
): Promise<any[]> => {
  try {
    if (!defaultTyreSize) {
      return [];
    }

    const { data, error } = await supabase
      .from('tyre_specifications')
      .select('*')
      .eq('size', defaultTyreSize)
      .order('brand', { ascending: true })
      .order('name', { ascending: true })
      .limit(20);

    if (error) {
      console.error('Error fetching compatible tyres:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Failed to fetch compatible tyres:', err);
    return [];
  }
};

/**
 * Get vehicle details by ID
 */
export const getVehicleById = async (vehicleId: string): Promise<VehicleModel | null> => {
  try {
    const { data, error } = await supabase
      .from('vehicle_models')
      .select(`
        *,
        vehicle_makes!inner(name)
      `)
      .eq('id', vehicleId)
      .single();

    if (error) {
      console.error('Error fetching vehicle details:', error);
      return null;
    }

    if (data) {
      const vehicleData = data as any;
      return {
        id: vehicleData.id,
        make_id: vehicleData.make_id,
        name: vehicleData.name,
        year_from: vehicleData.year_from,
        year_to: vehicleData.year_to,
        default_tyre_size: vehicleData.default_tyre_size,
        created_at: vehicleData.created_at,
        make_name: vehicleData.vehicle_makes?.name || 'Unknown'
      };
    }

    return null;
  } catch (err) {
    console.error('Failed to fetch vehicle details:', err);
    return null;
  }
};

/**
 * Get popular vehicle makes (prioritized in suggestions)
 */
export const getPopularVehicleMakes = async (): Promise<VehicleMake[]> => {
  try {
    const { data, error } = await supabase
      .from('vehicle_makes')
      .select('*')
      .eq('popular', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching popular vehicle makes:', error);
      return [];
    }

    return data || [];
  } catch (err) {
    console.error('Failed to fetch popular vehicle makes:', err);
    return [];
  }
};

/**
 * Fuzzy search across both makes and models
 */
export const fuzzyVehicleSearch = async (query: string): Promise<{
  makes: VehicleMake[];
  models: VehicleModel[];
}> => {
  if (!query.trim()) {
    return { makes: [], models: [] };
  }

  try {
    // Search makes and models concurrently
    const [makesPromise, modelsPromise] = await Promise.allSettled([
      getVehicleMakeSuggestions(query),
      getVehicleModelSuggestions(undefined, undefined, query)
    ]);

    const makes = makesPromise.status === 'fulfilled' ? makesPromise.value : [];
    const models = modelsPromise.status === 'fulfilled' ? modelsPromise.value : [];

    return { makes, models };
  } catch (err) {
    console.error('Failed to perform fuzzy vehicle search:', err);
    return { makes: [], models: [] };
  }
};